import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useClaimsFiltersSchema,
  useSignalEventOccurrencesFiltersSchema,
} from "shared/hooks";

import FilterBuilder from "features/ui/Filters/FilterBuilder";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { filterBuilderQueryToFilterBuilderState } from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";
import { OccursWindowOptions } from "features/ui/Filters/types";

import OccursTimeWindowForm from "./OccursTimeWindowForm";
import {
  decodeOccursFilterAndOptions,
  encodeOccursFilterAndOptions,
} from "./utils";

interface Props {
  fieldName: string;
  onChange: (values: string[]) => void;
  disabled?: boolean;
  currentFilterValues: string[];
  isAdvancedFilterEditor?: boolean;
  pageKey?: string;
  baseEntityText?: string;
  autoFocus?: boolean;
  hasChanges?: boolean;
  disableSelectingWindowDirection?: boolean;
  showThatOccurPrefix?: boolean;
}

// This component is very similar to Exists Filter, we can combine it at some point.
const OccursFilter = ({
  baseEntityText: baseEntityTextProp,
  fieldName,
  onChange,
  isAdvancedFilterEditor,
  currentFilterValues = [],
  disabled,
  pageKey = "",
  autoFocus,
  hasChanges,
  disableSelectingWindowDirection,
  showThatOccurPrefix,
}: Props) => {
  const { t } = useTranslation();
  const signalEventSchema = useSignalEventOccurrencesFiltersSchema();
  const claimsSchema = useClaimsFiltersSchema();

  const baseEntityText = baseEntityTextProp ?? t("filters:occurs.base_object");

  const getTimeWindowSchema = (entityText: string) => {
    const lowerText = entityText.toLowerCase();

    if (lowerText.includes("claim")) {
      return claimsSchema;
    }

    if (lowerText.includes("signal")) {
      return signalEventSchema;
    }

    return undefined;
  };

  const occursPageKey = `${pageKey}-occurs-${fieldName}`;

  // determine schema based on fieldName
  const schema =
    fieldName === "relatedClaims" ? claimsSchema : signalEventSchema;

  const timeWindowSchema = getTimeWindowSchema(baseEntityText);

  const { filters, windowSize, windowType, windowDirection, dateField } =
    decodeOccursFilterAndOptions(currentFilterValues[0]);

  const existsFilterSortState = useFilterSortState({
    pageKey: occursPageKey,
    defaultFilterValues: filterBuilderQueryToFilterBuilderState(filters),
    disableUsingQuery: true,
    disableUsingLocalStorage: true,
    onlyAdvancedFilters: true,
    schemaAttributes: schema.map(({ accessor }) => accessor),
  });

  const [occursOptionsState, setOccursOptionsState] =
    useState<OccursWindowOptions>({
      windowSize,
      windowType,
      windowDirection,
      dateField,
    });

  const onApplyFilters = (newFilterState: FilterGroupState) => {
    existsFilterSortState.updateFilters(newFilterState, (filters) => {
      propagateChanges(filters, occursOptionsState);
    });
  };

  const propagateChanges = (
    filters: FilterGroupState,
    occursOptionsState: OccursWindowOptions
  ): void => {
    const encoded = encodeOccursFilterAndOptions(filters, occursOptionsState);
    onChange([encoded]);
  };

  const onWindowOptionsChange = (newOptions: OccursWindowOptions) => {
    setOccursOptionsState(newOptions);
    propagateChanges(existsFilterSortState.filters, newOptions);
  };

  return (
    <div className="w-full flex flex-col gap-2 mt-2 pb-1">
      <FilterBuilder
        hasInitialFocus={autoFocus}
        filterBuilderState={existsFilterSortState.advancedFilters}
        filtersSchema={schema}
        onChange={onApplyFilters}
        hasChanges={hasChanges}
        inline
        embedded
        disabled={disabled}
        occursFilterSelector={!isAdvancedFilterEditor}
      />
      <OccursTimeWindowForm
        occursOptionsState={occursOptionsState}
        onUpdate={onWindowOptionsChange}
        baseEntityText={baseEntityText}
        schema={timeWindowSchema}
        fieldName={fieldName}
        disableSelectingWindowDirection={disableSelectingWindowDirection}
        showThatOccurPrefix={showThatOccurPrefix}
      />
    </div>
  );
};

export default OccursFilter;
